import { ErrorCategory, ClassifiedError } from './errorTypes';
import { getErrorMessage } from './errorMessages';

export const classifyError = (error: unknown): ClassifiedError => {
  const err = error instanceof Error ? error : new Error(String(error));
  const errorMessage = err.message.toLowerCase();
  const errorString = String(error).toLowerCase();

  // Database errors
  if (
    errorMessage.includes('database') ||
    errorMessage.includes('sql') ||
    errorMessage.includes('constraint') ||
    errorMessage.includes('unique violation') ||
    errorMessage.includes('foreign key') ||
    errorMessage.includes('relation') ||
    errorMessage.includes('column') ||
    errorMessage.includes('lock') ||
    errorMessage.includes('deadlock') ||
    errorString.includes('23505') || // Unique violation
    errorString.includes('23503') || // Foreign key violation
    errorString.includes('42p01') // Undefined table
  ) {
    return {
      category: ErrorCategory.DATABASE,
      message: err.message,
      userMessage: getErrorMessage(ErrorCategory.DATABASE),
      originalError: err,
      retryable: errorMessage.includes('lock') || errorMessage.includes('timeout'),
      code: extractPostgresCode(errorString)
    };
  }

  // Network errors
  if (
    errorMessage.includes('network') ||
    errorMessage.includes('fetch') ||
    errorMessage.includes('timeout') ||
    errorMessage.includes('connection') ||
    errorMessage.includes('offline') ||
    errorMessage.includes('econnrefused') ||
    errorMessage.includes('enotfound') ||
    errorString.includes('networkerror') ||
    errorString.includes('failed to fetch')
  ) {
    return {
      category: ErrorCategory.NETWORK,
      message: err.message,
      userMessage: getErrorMessage(ErrorCategory.NETWORK),
      originalError: err,
      retryable: true
    };
  }

  // Permission/Authorization errors
  if (
    errorMessage.includes('permission') ||
    errorMessage.includes('unauthorized') ||
    errorMessage.includes('forbidden') ||
    errorMessage.includes('access denied') ||
    errorMessage.includes('row-level security') ||
    errorMessage.includes('rls') ||
    errorMessage.includes('not authorized') ||
    errorString.includes('42501') || // Insufficient privilege
    errorString.includes('403')
  ) {
    return {
      category: ErrorCategory.PERMISSION,
      message: err.message,
      userMessage: getErrorMessage(ErrorCategory.PERMISSION),
      originalError: err,
      retryable: false
    };
  }

  // Authentication errors
  if (
    errorMessage.includes('authentication') ||
    errorMessage.includes('unauthenticated') ||
    errorMessage.includes('invalid credentials') ||
    errorMessage.includes('login') ||
    errorMessage.includes('session') ||
    errorMessage.includes('token') ||
    errorString.includes('401')
  ) {
    return {
      category: ErrorCategory.AUTHENTICATION,
      message: err.message,
      userMessage: getErrorMessage(ErrorCategory.AUTHENTICATION),
      originalError: err,
      retryable: false
    };
  }

  // Validation errors (including trigger validation)
  if (
    errorMessage.includes('validation') ||
    errorMessage.includes('invalid') ||
    errorMessage.includes('required') ||
    errorMessage.includes('must be') ||
    errorMessage.includes('should be') ||
    errorMessage.includes('format') ||
    errorMessage.includes('out of range') ||
    errorMessage.includes('minimum') ||
    errorMessage.includes('maximum') ||
    errorMessage.includes('p0001') || // Raise exception (used by our triggers)
    errorString.includes('p0001')
  ) {
    return {
      category: ErrorCategory.VALIDATION,
      message: err.message,
      userMessage: err.message, // Use specific validation message
      originalError: err,
      retryable: false,
      field: extractFieldName(err.message)
    };
  }

  // Rate limit errors
  if (
    errorMessage.includes('rate limit') ||
    errorMessage.includes('too many requests') ||
    errorString.includes('429')
  ) {
    return {
      category: ErrorCategory.RATE_LIMIT,
      message: err.message,
      userMessage: getErrorMessage(ErrorCategory.RATE_LIMIT),
      originalError: err,
      retryable: true
    };
  }

  // Not found errors
  if (
    errorMessage.includes('not found') ||
    errorMessage.includes('does not exist') ||
    errorString.includes('404')
  ) {
    return {
      category: ErrorCategory.NOT_FOUND,
      message: err.message,
      userMessage: getErrorMessage(ErrorCategory.NOT_FOUND),
      originalError: err,
      retryable: false
    };
  }

  // Unknown error
  return {
    category: ErrorCategory.UNKNOWN,
    message: err.message,
    userMessage: getErrorMessage(ErrorCategory.UNKNOWN),
    originalError: err,
    retryable: false
  };
};

const extractPostgresCode = (errorString: string): string | undefined => {
  const codeMatch = errorString.match(/\b(\d{5})\b/);
  return codeMatch ? codeMatch[1] : undefined;
};

const extractFieldName = (message: string): string | undefined => {
  // Try to extract field name from validation messages
  const fieldMatch = message.match(/field[:\s]+['"]?(\w+)['"]?/i);
  if (fieldMatch) return fieldMatch[1];
  
  const columnMatch = message.match(/column[:\s]+['"]?(\w+)['"]?/i);
  if (columnMatch) return columnMatch[1];
  
  return undefined;
};
